import { Badge, Button, Drawer, Group, Select, Stack, Text, TextInput } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { PortalMaterialOrderStatusEnum } from '@routellers/types/dist/partners-portal/enum/portal-material-order-status.enum';
import { PortalMaterialOrderRO } from '@routellers/types/dist/partners-portal/portal-material-order.ro';
import { PortalPartnerRO } from '@routellers/types/dist/partners-portal/portal-partner.ro';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { api, apiErrorMessage } from '../api';
import {
    formatDate,
    orderStatusColors,
    orderStatusLabel,
    orderStatusOptions,
} from '../utils/portal';

type OrderRow = PortalMaterialOrderRO & { partner?: PortalPartnerRO };

type Props = {
    opened: boolean;
    onClose: () => void;
    order: OrderRow | null;
    onSaved: () => void;
};

export const MaterialOrderEditor = ({ opened, onClose, order, onSaved }: Props) => {
    const { t } = useTranslation();
    const statusOptions = useMemo(() => orderStatusOptions(t), [t]);
    const [saving, setSaving] = useState(false);
    const [status, setStatus] = useState<PortalMaterialOrderStatusEnum | null>(null);
    const [trackingInfo, setTrackingInfo] = useState('');

    useEffect(() => {
        if (!opened || !order) return;

        setStatus(order.status);
        setTrackingInfo(order.trackingInfo || '');
    }, [opened, order]);

    const save = async () => {
        if (!order || !status) return;

        setSaving(true);

        try {
            await api.partnersPortalAdminApi.updateMaterialOrder({
                id: order.id,
                data: {
                    status,
                    trackingInfo: trackingInfo.trim() || undefined,
                },
            });

            notifications.show({ color: 'green', message: t('editors.materialOrder.updated') });
            onSaved();
            onClose();
        } catch (error) {
            notifications.show({ color: 'red', message: apiErrorMessage(error) });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Drawer
            opened={opened}
            onClose={onClose}
            title={
                order
                    ? t('editors.materialOrder.title', { id: order.id })
                    : t('editors.materialOrder.fallbackTitle')
            }
            position='right'
            size='md'
            padding='md'
        >
            {!order ? null : (
                <Stack gap='md'>
                    <Group gap='sm'>
                        <Badge color={orderStatusColors[order.status] || 'gray'} variant='light'>
                            {orderStatusLabel(t, order.status) || order.status}
                        </Badge>
                        <Text size='sm' c='dimmed'>
                            {formatDate(order.createdAt)}
                        </Text>
                    </Group>

                    <Stack gap={4}>
                        <Text size='sm'>
                            {t('editors.materialOrder.venue', {
                                name: order.partner
                                    ? order.partner.companyName || order.partner.name
                                    : `#${order.partnerId}`,
                            })}
                        </Text>
                        <Text size='sm' c='dimmed'>
                            {t('editors.materialOrder.quantity', { count: order.quantity })}
                        </Text>
                        {order.address && (
                            <Text size='sm' c='dimmed'>
                                {t('editors.materialOrder.address', { address: order.address })}
                            </Text>
                        )}
                        {order.comment && (
                            <Text size='sm' c='dimmed'>
                                {t('editors.materialOrder.comment', { comment: order.comment })}
                            </Text>
                        )}
                    </Stack>

                    <Select
                        label={t('editors.materialOrder.status')}
                        data={statusOptions}
                        value={status}
                        onChange={(v) =>
                            setStatus((v as PortalMaterialOrderStatusEnum) || order.status)
                        }
                    />

                    <TextInput
                        label={t('editors.materialOrder.trackingInfo')}
                        value={trackingInfo}
                        onChange={(e) => setTrackingInfo(e.currentTarget.value)}
                    />

                    <Group justify='flex-end' mt='md'>
                        <Button variant='default' onClick={onClose}>
                            {t('editors.materialOrder.cancel')}
                        </Button>
                        <Button loading={saving} onClick={save}>
                            {t('editors.materialOrder.save')}
                        </Button>
                    </Group>
                </Stack>
            )}
        </Drawer>
    );
};
